import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { BackButton } from '@/components/BackButton';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Loader2, Lock, UserPlus, Crown, Flag } from 'lucide-react';
import { categories, getIconComponent } from '@/data/quickTipsData';
import { ContentGateModal, gateFor } from '@/components/ContentGate';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import SEO from '@/components/SEO';
import { PageBreadcrumbs } from '@/components/StructuredData';
import RelatedPages from '@/components/RelatedPages';
import { toast } from 'sonner';

import { API_URL as API } from '@/config';
import { getPrefetched, getResolvedData, prefetchDetail } from '@/utils/dataPrefetcher';

// Translation helper for all 9 languages
const texts = {
  quickTips: { en: 'Quick Tips', ar: 'نصائح سريعة', sv: 'Snabba Råd', tr: 'Hızlı İpuçları', fa: 'نکات سریع', so: 'Talooyin Degdeg ah', pl: 'Szybkie Porady', es: 'Consejos Rápidos', de: 'Schnelle Tipps' },
  goldenTips: { en: 'golden tips to pass the driving theory test', ar: 'نصيحة ذهبية لاجتياز اختبار القيادة النظري', sv: 'gyllene tips för att klara körkortsteorin', tr: 'sürüş teorisi sınavını geçmek için altın ipuçları', fa: 'نکته طلایی برای قبولی در آزمون تئوری رانندگی', so: 'talooyinka dahabiga ah ee lagu guuleysanayo imtixaanka aragti-wadista', pl: 'złotych porad, aby zdać egzamin teoretyczny', es: 'consejos de oro para aprobar el examen teórico de conducir', de: 'goldene Tipps, um die Fahrtheorieprüfung zu bestehen' },
  subscriptionRequired: { en: 'Subscription Required', ar: 'اشتراك مطلوب', sv: 'Prenumeration krävs', tr: 'Abonelik Gerekli', fa: 'اشتراک مورد نیاز است', so: 'Qormada loo baahan yahay', pl: 'Wymagana subskrypcja', es: 'Suscripción Requerida', de: 'Abonnement erforderlich' },
  subscribeDesc: { en: 'Subscribe now to access all quick tips and premium content', ar: 'اشترك الآن للوصول لجميع النصائح السريعة والمحتوى المميز', sv: 'Prenumerera nu för att få tillgång till alla snabba råd och premiuminnehåll', tr: 'Tüm hızlı ipuçlarına ve premium içeriğe erişmek için şimdi abone olun', fa: 'برای دسترسی به همه نکات سریع و محتوای ویژه اشتراک بگیرید', so: 'Hadda iska qor si aad u hesho dhammaan talooyin degdeg ah iyo waxyaabo gaar ah', pl: 'Subskrybuj teraz, aby uzyskać dostęp do wszystkich szybkich porad i treści premium', es: 'Suscríbete ahora para acceder a todos los consejos rápidos y contenido premium', de: 'Abonnieren Sie jetzt, um auf alle schnellen Tipps und Premium-Inhalte zuzugreifen' },
  subscribeNow: { en: 'Subscribe Now', ar: 'اشترك الآن', sv: 'Prenumerera nu', tr: 'Şimdi Abone Ol', fa: 'اشتراک', so: 'Hadda Iska Qor', pl: 'Subskrybuj Teraz', es: 'Suscríbete Ahora', de: 'Jetzt Abonnieren' },
  later: { en: 'Later', ar: 'لاحقاً', sv: 'Senare', tr: 'Daha Sonra', fa: 'بعداً', so: 'Kadib', pl: 'Później', es: 'Más Tarde', de: 'Später' },
  subscriptionRequiredTips: { en: 'Subscription required to access tips', ar: 'يجب الاشتراك للوصول للنصائح', sv: 'Prenumeration krävs', tr: 'İpuçlarına erişmek için abonelik gerekli', fa: 'برای دسترسی به نکات اشتراک لازم است', so: 'Qormada loo baahan yahay si aad u gasho talooyinka', pl: 'Wymagana subskrypcja do uzyskania porad', es: 'Suscripción requerida para acceder a los consejos', de: 'Abonnement erforderlich für Tipps' },
  showing: { en: 'Showing', ar: 'عرض', sv: 'Visar', tr: 'Gösteriliyor', fa: 'نمایش', so: 'Muujinaya', pl: 'Pokazano', es: 'Mostrando', de: 'Zeigt' },
  of: { en: 'of', ar: 'من', sv: 'av', tr: '/', fa: 'از', so: 'ka mid ah', pl: 'z', es: 'de', de: 'von' },
  tips: { en: 'tips', ar: 'نصيحة', sv: 'tips', tr: 'ipucu', fa: 'نکته', so: 'talooyinka', pl: 'porad', es: 'consejos', de: 'Tipps' },
  readyToPractice: { en: 'Ready to practice?', ar: 'جاهز للتطبيق؟', sv: 'Redo att öva?', tr: 'Pratik yapmaya hazır mısın?', fa: 'آماده تمرین هستید؟', so: 'Ma diyaar u tahay inaad tababarto?', pl: 'Gotowy do ćwiczeń?', es: '¿Listo para practicar?', de: 'Bereit zum Üben?' },
  testKnowledge: { en: 'Test your knowledge now!', ar: 'اختبر معلوماتك الآن!', sv: 'Testa dina kunskaper nu!', tr: 'Bilgini şimdi test et!', fa: 'دانش خود را الان آزمایش کنید!', so: 'Imtixaan aqoontaada hadda!', pl: 'Sprawdź swoją wiedzę teraz!', es: '¡Pon a prueba tus conocimientos ahora!', de: 'Testen Sie jetzt Ihr Wissen!' },
  startTest: { en: 'Start Test', ar: 'ابدأ الاختبار', sv: 'Starta prov', tr: 'Teste Başla', fa: 'شروع آزمون', so: 'Bilow Imtixaanka', pl: 'Rozpocznij Test', es: 'Comenzar Prueba', de: 'Test Starten' },
  reportError: { en: 'Report Error', ar: 'الإبلاغ عن خطأ', sv: 'Rapportera fel', tr: 'Hata Bildir', fa: 'گزارش خطا', so: 'Ka warbixin khalad', pl: 'Zgłoś błąd', es: 'Reportar Error', de: 'Fehler melden' },
  reportTitle: { en: 'Report an Error', ar: 'الإبلاغ عن خطأ', sv: 'Rapportera ett fel', tr: 'Bir Hata Bildir', fa: 'گزارش یک خطا', so: 'Ka warbixin khalad', pl: 'Zgłoś błąd', es: 'Reportar un error', de: 'Einen Fehler melden' },
  reportType: { en: 'Report Type', ar: 'نوع البلاغ', sv: 'Typ av rapport', tr: 'Rapor Türü', fa: 'نوع گزارش', so: 'Nooca warbixinta', pl: 'Typ raportu', es: 'Tipo de reporte', de: 'Berichttyp' },
  incorrectInfo: { en: 'Incorrect Information', ar: 'معلومات غير صحيحة', sv: 'Felaktig information', tr: 'Yanlış Bilgi', fa: 'اطلاعات نادرست', so: 'Macluumaad khaldan', pl: 'Nieprawidłowe informacje', es: 'Información incorrecta', de: 'Falsche Information' },
  imageIssue: { en: 'Image Issue', ar: 'مشكلة في الصورة', sv: 'Bildproblem', tr: 'Resim Sorunu', fa: 'مشکل تصویر', so: 'Dhibaato sawir', pl: 'Problem z obrazem', es: 'Problema de imagen', de: 'Bildproblem' },
  translationError: { en: 'Translation Error', ar: 'خطأ في الترجمة', sv: 'Översättningsfel', tr: 'Çeviri Hatası', fa: 'خطای ترجمه', so: 'Khalad turjumaad', pl: 'Błąd tłumaczenia', es: 'Error de traducción', de: 'Übersetzungsfehler' },
  otherIssue: { en: 'Other Issue', ar: 'مشكلة أخرى', sv: 'Annat problem', tr: 'Diğer Sorun', fa: 'مشکل دیگر', so: 'Dhibaato kale', pl: 'Inny problem', es: 'Otro problema', de: 'Anderes Problem' },
  details: { en: 'Details', ar: 'التفاصيل', sv: 'Detaljer', tr: 'Detaylar', fa: 'جزئیات', so: 'Faahfaahin', pl: 'Szczegóły', es: 'Detalles', de: 'Details' },
  describeIssue: { en: 'Please describe the issue...', ar: 'يرجى وصف المشكلة...', sv: 'Beskriv problemet...', tr: 'Lütfen sorunu açıklayın...', fa: 'لطفاً مشکل را شرح دهید...', so: 'Fadlan sharax dhibaatada...', pl: 'Proszę opisać problem...', es: 'Por favor describa el problema...', de: 'Bitte beschreiben Sie das Problem...' },
  submit: { en: 'Submit', ar: 'إرسال', sv: 'Skicka', tr: 'Gönder', fa: 'ارسال', so: 'Dir', pl: 'Wyślij', es: 'Enviar', de: 'Absenden' },
  cancel: { en: 'Cancel', ar: 'إلغاء', sv: 'Avbryt', tr: 'İptal', fa: 'لغو', so: 'Jooji', pl: 'Anuluj', es: 'Cancelar', de: 'Abbrechen' },
  reportSuccess: { en: 'Report submitted successfully!', ar: 'تم إرسال البلاغ بنجاح!', sv: 'Rapporten har skickats!', tr: 'Rapor başarıyla gönderildi!', fa: 'گزارش با موفقیت ارسال شد!', so: 'Warbixinta si guul leh ayaa loo diray!', pl: 'Raport wysłany pomyślnie!', es: '¡Reporte enviado con éxito!', de: 'Bericht erfolgreich gesendet!' },
  reportFailed: { en: 'Failed to submit report', ar: 'فشل في إرسال البلاغ', sv: 'Det gick inte att skicka rapporten', tr: 'Rapor gönderilemedi', fa: 'ارسال گزارش ناموفق بود', so: 'Waa lagu guul daraystay dirista warbixinta', pl: 'Nie udało się wysłać raportu', es: 'Error al enviar el reporte', de: 'Bericht konnte nicht gesendet werden' },
};

const getText = (key, lang) => texts[key]?.[lang] || texts[key]?.en || key;

const QuickTipsPage = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const lang = i18n.language;
  const isRTL = lang === 'ar' || lang === 'fa';

  const [selectedCategory, setSelectedCategory] = useState('all');
  const [tips, setTips] = useState(() => getResolvedData('quickTips') || []);
  const [loading, setLoading] = useState(() => !getResolvedData('quickTips'));
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportingTip, setReportingTip] = useState(null);
  const [reportType, setReportType] = useState('incorrect_info');
  const [reportDetails, setReportDetails] = useState('');
  const [submittingReport, setSubmittingReport] = useState(false);

  // التحقق إذا كان المستخدم مشترك
  const isSubscribed = user?.subscription_status === 'active' || user?.taxi_subscription_status === 'active' || user?.is_admin || user?.role === 'super_admin';

  useEffect(() => {
    const fetchTips = async () => {
      try {
        // Only use prefetch for Swedish (default prefetch language)
        if (i18n.language === 'sv') {
          const prefetched = await getPrefetched('quickTips');
          if (prefetched) {
            setTips(prefetched);
            setLoading(false);
            return;
          }
        }
        const response = await axios.get(`${API}/api/quick-tips?lang=${i18n.language || 'sv'}`);
        setTips(response.data || []);
      } catch (error) {
        console.error('Error fetching tips:', error);
        import('@/data/quickTipsData').then(module => {
          setTips(module.tips || []);
        });
      } finally {
        setLoading(false);
      }
    };
    fetchTips();
  }, [i18n.language]);

  // تحميل تفاصيل أول نصيحتين مجانيتين تلقائياً
  useEffect(() => {
    if (tips.length > 0) {
      tips.slice(0, 2).forEach(tip => {
        prefetchDetail('tip', tip.slug || tip.id);
      });
    }
  }, [tips]);

  // Generate slug from Swedish title
  const generateSlug = (title) => {
    if (!title) return '';
    const sv = typeof title === 'string' ? title : (title.sv || title.en || '');
    return sv.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim()
      .replace(/[^a-z0-9\s-]/g, '').replace(/[\s_]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
  };

  const getTipPath = (tip) => {
    const slug = generateSlug(tip.title);
    const langPaths = {
      sv: '/snabba-rad', ar: '/ar/nasaeh-sareea', en: '/en/quick-tips', tr: '/tr/hizli-ipuclari',
      fa: '/fa/nookat-sari', so: '/so/talooyinka-degdegga', de: '/de/schnelle-tipps', es: '/es/consejos-rapidos', pl: '/pl/szybkie-porady',
    };
    return `${langPaths[i18n.language] || langPaths.sv}/${slug || tip.id}`;
  };

  // دالة للتعامل مع النقر على النصيحة
  const handleTipClick = (tip, gate = 'plans') => {
    if (isSubscribed || gate === 'open') {
      navigate(getTipPath(tip), { state: { tipData: tip } });
    } else {
      setShowSubscribeModal(gate);
    }
  };

  // دالة للإبلاغ عن خطأ
  const handleReportClick = (e, tip) => {
    e.stopPropagation();
    setReportingTip(tip);
    setReportType('incorrect_info');
    setReportDetails('');
    setShowReportModal(true);
  };

  // دالة لإرسال البلاغ
  const handleSubmitReport = async () => {
    if (!reportDetails.trim()) return;
    
    setSubmittingReport(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/site-reports`,
        {
          url: `/snabba-tips`,
          report_type: reportType,
          details: `[Quick Tip: ${reportingTip?.title?.en || reportingTip?.id}] ${reportDetails}`,
          item_id: reportingTip?.id,
          item_type: 'quick_tip'
        },
        token ? { headers: { Authorization: `Bearer ${token}` } } : {}
      );
      toast.success(getText('reportSuccess', lang));
      setShowReportModal(false);
      setReportingTip(null);
    } catch (error) {
      console.error('Error submitting report:', error);
      toast.error(getText('reportFailed', lang));
    } finally {
      setSubmittingReport(false);
    }
  };

  const filteredTips = selectedCategory === 'all' 
    ? tips 
    : tips.filter(t => t.category === selectedCategory);

  if (loading && tips.length === 0) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 dark:bg-slate-950" dir={isRTL ? 'rtl' : 'ltr'}>
      <SEO page="quick-tips" />
      <Navbar />
      
      {/* Subscription Modal */}
      <ContentGateModal mode={showSubscribeModal || null} onClose={() => setShowSubscribeModal(false)} lang={lang} />
      
      <main className="flex-grow container mx-auto px-3 sm:px-4 py-4 sm:py-8 mt-16 sm:mt-20">
        <BackButton to="/" className="mb-4" />
        
        {/* Header */}
        <div className="text-center mb-4 sm:mb-8">
          <div className="flex items-center justify-center gap-2 sm:gap-3 mb-2 sm:mb-3">
            <h1 className="text-xl sm:text-3xl lg:text-4xl font-bold text-slate-900 dark:text-slate-100">
              {getText('quickTips', lang)} 💡
            </h1>
          </div>
          <p className="text-xs sm:text-base text-slate-600 dark:text-slate-400 max-w-2xl mx-auto px-2 hidden sm:block">
            {tips.length} {getText('goldenTips', lang)}
          </p>
          {!isSubscribed && (
            <div className="mt-3 sm:mt-5 inline-flex items-center gap-2 sm:gap-2.5 bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400 px-4 sm:px-5 py-2 sm:py-2.5 rounded-full text-sm sm:text-base font-medium border border-transparent dark:border-amber-800/30">
              <Lock className="w-4 h-4 sm:w-5 sm:h-5" />
              {getText('subscriptionRequiredTips', lang)}
            </div>
          )}
        </div>

        {/* Category Filter */}
        <div className="mb-4 sm:mb-6 -mx-3 px-3 overflow-x-auto scrollbar-hide">
          <div className="flex gap-2 justify-start sm:justify-center pb-2 min-w-max sm:min-w-0 sm:flex-wrap">
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-2.5 py-1 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${
                  selectedCategory === cat.id
                    ? 'bg-amber-500 text-white shadow-md'
                    : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-amber-50 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-600'
                }`}
              >
                <span className="me-1">{cat.icon}</span>
                {cat.label[lang]}
              </button>
            ))}
          </div>
        </div>

        {/* Stats */}
        <div className="text-center mb-3 sm:mb-6 text-xs sm:text-sm text-slate-500 dark:text-slate-400">
          {getText('showing', lang)} {filteredTips.length} {getText('of', lang)} {tips.length} {getText('tips', lang)}
        </div>

        {/* Tips Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-4">
          {filteredTips.map((tip, index) => {
            // Handle both component and string icon names
            const Icon = typeof tip.icon === 'string' ? getIconComponent(tip.icon) : tip.icon;
            // أول بطاقتين مجانيتين للزوار والمستخدمين المجانيين
            const gate = gateFor(index, user, isSubscribed);
            const showLock = gate !== 'open';
            
            return (
              <div 
                key={tip.id}
                onClick={() => handleTipClick(tip, gate)}
                data-testid={`tip-card-${index}`}
                onMouseEnter={() => {
                  const slug = generateSlug(tip.title);
                  prefetchDetail('tip', slug || tip.id);
                }}
                className="group bg-white dark:bg-slate-800 rounded-xl sm:rounded-2xl border-2 border-slate-100 dark:border-slate-700 hover:border-amber-400 dark:hover:border-amber-600 hover:shadow-lg transition-all duration-200 cursor-pointer overflow-hidden relative"
              >
                {/* Lock overlay for non-subscribers (except first 2) */}
                {showLock && (
                  <div className="absolute inset-0 bg-slate-900/40 dark:bg-black/60 backdrop-blur-[1px] z-10 flex items-center justify-center cursor-pointer" onClick={(e) => { e.stopPropagation(); setShowSubscribeModal(gate); }} data-testid="content-lock-overlay">
                    <div className="bg-white/90 dark:bg-slate-700/90 rounded-full p-2">
                      {gate === 'register' ? <UserPlus className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 dark:text-blue-300" /> : <Lock className="w-4 h-4 sm:w-5 sm:h-5 text-slate-700 dark:text-slate-300" />}
                    </div>
                  </div>
                )}
                
                {/* Report Button */}
                <button
                  onClick={(e) => handleReportClick(e, tip)}
                  className={`absolute top-1 ${isRTL ? 'right-1' : 'left-1'} z-20 p-1.5 sm:p-2 bg-orange-500 hover:bg-orange-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-md`}
                  title={getText('reportError', lang)}
                  data-testid={`report-tip-${tip.id}`}
                >
                  <Flag className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                </button>
                
                {/* Color bar */}
                <div className={`h-1 sm:h-1.5 ${tip.color}`} />
                
                {/* Image or Icon area */}
                <div className={`aspect-[4/3] ${!tip.image_url ? tip.color + ' bg-opacity-10' : ''} flex items-center justify-center relative overflow-hidden`}>
                  {tip.image_url ? (
                    <img 
                      src={tip.image_url} 
                      alt={typeof tip.title === 'string' ? tip.title : tip.title?.[lang]}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className={`w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl ${tip.color} flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg`}>
                      <Icon className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                    </div>
                  )}
                </div>

                {/* Title */}
                <div className="p-2 sm:p-3">
                  <h3 className="font-semibold text-xs sm:text-sm text-slate-900 dark:text-slate-100 line-clamp-2 leading-tight">
                    {typeof tip.title === 'string' ? tip.title : tip.title?.[lang]}
                  </h3>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="mt-6 sm:mt-8 text-center">
          <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl sm:rounded-2xl p-4 sm:p-6 text-white">
            <h3 className="text-base sm:text-xl font-bold mb-2">
              {getText('readyToPractice', lang)}
            </h3>
            <p className="text-emerald-100 mb-3 sm:mb-4 text-xs sm:text-base">
              {getText('testKnowledge', lang)}
            </p>
            <Button
              onClick={() => navigate('/teoriprov')}
              className="bg-white text-emerald-600 hover:bg-emerald-50 font-bold px-6 sm:px-8 text-xs sm:text-base"
            >
              {getText('startTest', lang)}
            </Button>
          </div>
        </div>
      </main>

      {/* مودال الإبلاغ عن خطأ */}
      {showReportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-6 relative" dir={isRTL ? 'rtl' : 'ltr'}>
            <button 
              onClick={() => setShowReportModal(false)}
              className={`absolute top-4 ${isRTL ? 'left-4' : 'right-4'} w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors`}
            >
              ✕
            </button>
            
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-orange-100 dark:bg-orange-950/30 rounded-full flex items-center justify-center">
                <Flag className="w-6 h-6 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">{getText('reportTitle', lang)}</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">{reportingTip?.title?.[lang] || reportingTip?.title?.en}</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {getText('reportType', lang)}
                </label>
                <select
                  value={reportType}
                  onChange={(e) => setReportType(e.target.value)}
                  className="w-full p-3 border border-slate-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                >
                  <option value="incorrect_info">{getText('incorrectInfo', lang)}</option>
                  <option value="image_issue">{getText('imageIssue', lang)}</option>
                  <option value="translation_error">{getText('translationError', lang)}</option>
                  <option value="other">{getText('otherIssue', lang)}</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {getText('details', lang)}
                </label>
                <textarea
                  value={reportDetails}
                  onChange={(e) => setReportDetails(e.target.value)}
                  placeholder={getText('describeIssue', lang)}
                  rows={4}
                  className="w-full p-3 border border-slate-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowReportModal(false)}
                  className="flex-1 py-3 border-2 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 font-semibold rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700"
                >
                  {getText('cancel', lang)}
                </button>
                <button
                  onClick={handleSubmitReport}
                  disabled={!reportDetails.trim() || submittingReport}
                  className="flex-1 py-3 bg-orange-500 hover:bg-orange-600 disabled:bg-slate-300 dark:disabled:bg-slate-600 text-white font-semibold rounded-xl transition-colors"
                >
                  {submittingReport ? '...' : getText('submit', lang)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 sm:px-6"><RelatedPages currentPage="tips" /></div>
      <Footer />
    </div>
  );
};

export default QuickTipsPage;
